import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { useState } from "react";
import {
  ArtifactType,
  CoachingDimensionNode,
  CoachingEvidenceNode,
  CoachingInsightNode,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import AppLink from "@components/link/link";

import { DimensionIcon, StatusBadge } from "./coaching-components";
import { priorityBadge } from "./coaching-constants";
import type { Priority, Status } from "./coaching-constants";

const InsightCard = ({ insight }: { insight: CoachingInsightNode }) => {
  const badge = priorityBadge[(insight.priority ?? "low") as Priority];
  const label = useLabel();

  const getSourceLabel = (typeKey: string): string => {
    if (typeKey === "meeting") return label("oneonone");
    if (typeKey === "goal") return label(ArtifactType.Goal);
    if (typeKey === "recognition") return label(ArtifactType.Recognition);
    return typeKey;
  };

  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5 overflow-hidden flex flex-col">
      <div className="px-5 py-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <h4 className="text-base font-semibold text-gray-900">
            {insight.title}
          </h4>
          {badge && <StatusBadge status={badge.status} label={badge.label} />}
        </div>

        <p className="text-sm leading-6 text-gray-600">{insight.body}</p>

        {(insight.evidence?.filter(
          (ev): ev is CoachingEvidenceNode => ev != null,
        ).length ?? 0) > 0 && (
          <div className="flex flex-col gap-2">
            {insight.evidence
              ?.filter((ev): ev is CoachingEvidenceNode => ev != null)
              .map((ev, i) => (
                <div key={i} className="border-l-2 border-gray-200 pl-4 py-1">
                  <p className="text-sm italic text-gray-500 leading-6">
                    {ev.text}
                    {ev.sourceRef &&
                      (() => {
                        const url = ev.sourceUrl;
                        const sourceLabel = getSourceLabel(
                          ev.sourceRef.replace(/_\d+$/, ""),
                        );
                        return url ? (
                          <AppLink
                            to={url}
                            className="not-italic text-xs text-blue-600 hover:text-blue-700 ml-1.5 hover:underline"
                          >
                            View {sourceLabel}
                          </AppLink>
                        ) : null;
                      })()}
                  </p>
                </div>
              ))}
          </div>
        )}
      </div>

      {insight.action && (
        <div className="bg-gray-50 border-t border-gray-100 px-5 py-4 mt-auto">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-1">
            {insight.action.label}
          </p>
          <p className="text-sm leading-6 text-gray-700">
            {insight.action.text}
          </p>
        </div>
      )}
    </div>
  );
};

export const DimensionSection = ({
  dimension,
}: {
  dimension: CoachingDimensionNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        className="flex items-center justify-between gap-3 cursor-pointer group"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2.5">
          {isOpen ? (
            <ChevronDownIcon className="h-4 w-4 text-gray-400 shrink-0" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-gray-400 shrink-0" />
          )}
          <DimensionIcon
            name={dimension.name ?? ""}
            className="h-5 w-5 text-gray-500"
          />
          <h3 className="text-base font-semibold leading-7 text-gray-900">
            {dimension.name}
          </h3>
        </div>
        <StatusBadge
          status={(dimension.status ?? "good") as Status}
          label={dimension.status ?? ""}
        />
      </button>
      {isOpen && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {(dimension.insights ?? [])
            .filter(
              (insight): insight is CoachingInsightNode => insight != null,
            )
            .map((insight, i) => (
              <InsightCard key={insight.title ?? i} insight={insight} />
            ))}
        </div>
      )}
    </div>
  );
};
